import {
  getJalaliWeekDays,
  gregorianToJalali,
  jalaliDateKey,
  jalaliMonths,
  jalaliToGregorian,
  parseJalaliDateKey,
  todayJalali,
  gregorianWeekdaysToJalali,
  toPersianDigits,
  weekDaysFa,
  type JalaliDayRef,
} from './jalali';
import { gregorianWeekStartsOn, todayGregorian, weekdayLabelsForGregorian } from './gregorianCalendar';

export type CalendarPickerDate = { year: number; month: number; day: number };

/** Persian UI uses Jalali; English and Arabic use Gregorian in the picker. */
export function isJalaliPickerLocale(localeCode: string): boolean {
  return localeCode.split('-')[0] === 'fa';
}

/** Week strip days — Jalali Sat→Fri for `fa`, locale week start + Gregorian labels otherwise. */
export function getWeekStripDays(
  anchorYear: number,
  anchorMonth: number,
  anchorDay: number,
  localeCode: string,
): JalaliDayRef[] {
  if (isJalaliPickerLocale(localeCode)) {
    return getJalaliWeekDays(anchorYear, anchorMonth, anchorDay);
  }

  const base = localeCode.split('-')[0];
  const g = jalaliToGregorian(anchorYear, anchorMonth, anchorDay);
  const anchor = new Date(g.year, g.month - 1, g.day);
  anchor.setHours(12, 0, 0, 0);

  const weekStartsOn = gregorianWeekStartsOn(base);
  const offset = (anchor.getDay() - weekStartsOn + 7) % 7;
  const start = new Date(anchor);
  start.setDate(start.getDate() - offset);

  const todayG = todayGregorian();
  const labels = weekdayLabelsForGregorian(base);

  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const j = gregorianToJalali(d.getFullYear(), d.getMonth() + 1, d.getDate());
    return {
      year: j.year,
      month: j.month,
      day: j.dat,
      dateKey: jalaliDateKey(j.year, j.month, j.dat),
      dowLabel: labels[i] ?? '',
      isToday:
        d.getFullYear() === todayG.year &&
        d.getMonth() + 1 === todayG.month &&
        d.getDate() === todayG.day,
    };
  });
}

/** Day-of-month number shown in the week strip (Gregorian for non-`fa`). */
export function weekStripDisplayDay(day: JalaliDayRef, localeCode: string): number {
  if (isJalaliPickerLocale(localeCode)) return day.day;
  return jalaliToGregorian(day.year, day.month, day.day).day;
}

/** App storage format is always Jalali `Y/M/D`. */
export function jalaliKeyToPickerDate(key: string, useJalali: boolean): CalendarPickerDate | null {
  const j = parseJalaliDateKey(key);
  if (!j) return null;
  if (useJalali) return { year: j.year, month: j.month, day: j.day };
  const g = jalaliToGregorian(j.year, j.month, j.day);
  return { year: g.year, month: g.month, day: g.day };
}

export function todayAsPickerDate(useJalali: boolean): CalendarPickerDate {
  if (useJalali) {
    const t = todayJalali();
    return { year: t.year, month: t.month, day: t.dat };
  }
  return todayGregorian();
}

export function pickerDateToJalaliKey(date: CalendarPickerDate, useJalali: boolean): string {
  if (useJalali) return jalaliDateKey(date.year, date.month, date.day);
  const j = gregorianToJalali(date.year, date.month, date.day);
  return jalaliDateKey(j.year, j.month, j.dat);
}

export function formatPickerPreview(
  date: CalendarPickerDate,
  useJalali: boolean,
  localeCode: string,
): string {
  if (useJalali) {
    return `${date.year}/${String(date.month).padStart(2, '0')}/${String(date.day).padStart(2, '0')}`;
  }
  const d = new Date(date.year, date.month - 1, date.day);
  const base = localeCode.split('-')[0];
  const loc = base === 'ar' ? 'ar' : base === 'tr' ? 'tr' : 'en';
  return new Intl.DateTimeFormat(loc, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(d);
}

/** Parse `YYYY-MM-DD` from API datetime strings (`workout_day`, `created_at`, …). */
export function extractIsoDatePart(value: string): string {
  const m = value.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '';
}

/**
 * Compact locale-aware date for workout headers, comments, and chart legends.
 * `fa` → Jalali `1405/03/24`; English keeps `YYYY-MM-DD`; other locales use Intl.
 */
export function formatApiDateCompact(isoOrDatetime: string, localeCode: string): string {
  if (!isoOrDatetime) return '';
  const iso = extractIsoDatePart(isoOrDatetime);
  if (!iso) return isoOrDatetime;
  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return iso;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return iso;

  if (isJalaliPickerLocale(localeCode)) {
    const j = gregorianToJalali(y, m, d);
    const label = `${j.year}/${String(j.month).padStart(2, '0')}/${String(j.dat).padStart(2, '0')}`;
    return toPersianDigits(label);
  }

  const base = localeCode.split('-')[0];
  if (base === 'en') return iso;

  const loc = base === 'ar' ? 'ar' : base === 'tr' ? 'tr' : 'en';
  return new Intl.DateTimeFormat(loc, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(y, m - 1, d));
}

/** Calendar day list header — weekday + day + month, no year (year lives in toolbar month chip). */
export function formatIsoDateForCalendarDayHeader(iso: string, localeCode: string): string {
  if (!iso) return '';
  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return iso;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return iso;

  if (isJalaliPickerLocale(localeCode)) {
    const j = gregorianToJalali(y, m, d);
    const weekday = weekDaysFa[gregorianWeekdaysToJalali(new Date(y, m - 1, d).getDay())] ?? '';
    return toPersianDigits(`${weekday} ${j.dat} ${jalaliMonths[j.month] ?? ''}`.trim());
  }

  const date = new Date(y, m - 1, d);
  const base = localeCode.split('-')[0];
  const loc = base === 'ar' ? 'ar' : base === 'tr' ? 'tr' : 'en';
  return new Intl.DateTimeFormat(loc, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

/** Month chip on calendar toolbar — month name on first line, year on second. */
export function formatCalendarToolbarMonthYear(
  year: number,
  month: number,
  day: number,
  localeCode: string,
): { monthLine: string; yearLine: string } {
  if (isJalaliPickerLocale(localeCode)) {
    return {
      monthLine: jalaliMonths[month] ?? '',
      yearLine: String(year),
    };
  }

  const g = jalaliToGregorian(year, month, day);
  const date = new Date(g.year, g.month - 1, g.day);
  const base = localeCode.split('-')[0];
  const loc = base === 'ar' ? 'ar' : base === 'tr' ? 'tr' : 'en';
  return {
    monthLine: new Intl.DateTimeFormat(loc, { month: 'short' }).format(date),
    yearLine: new Intl.DateTimeFormat(loc, { year: 'numeric' }).format(date),
  };
}

/** Display label for workout-form date chip — Jalali + weekday for `fa`, Intl otherwise. */
export function formatIsoDateForDisplay(iso: string, localeCode: string, includeYear = true): string {
  if (!iso) return '';
  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return iso;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return iso;

  if (isJalaliPickerLocale(localeCode)) {
    const j = gregorianToJalali(y, m, d);
    const weekday = weekDaysFa[gregorianWeekdaysToJalali(new Date(y, m - 1, d).getDay())] ?? '';
    return toPersianDigits(`${weekday} ${j.dat} ${jalaliMonths[j.month] ?? ''} ${includeYear ? j.year : ''}`.trim());
  }

  const date = new Date(y, m - 1, d);
  const base = localeCode.split('-')[0];
  const loc = base === 'ar' ? 'ar' : base === 'tr' ? 'tr' : 'en';
  return new Intl.DateTimeFormat(loc, {
    weekday: 'short',
    year: includeYear ? 'numeric' : undefined,
    month: 'short',
    day: 'numeric',
  }).format(date);
}

/** Adjacent date picker supplies the year; retain both years at a calendar-year boundary. */
export function formatCalendarWeekRange(start: string, end: string, localeCode: string): string {
  const year = (iso: string) => {
    const [y, m, d] = iso.split('-').map(Number);
    return isJalaliPickerLocale(localeCode) ? gregorianToJalali(y, m, d).year : y;
  };
  const includeYear = year(start) !== year(end);
  return `${formatIsoDateForDisplay(start, localeCode, includeYear)} – ${formatIsoDateForDisplay(end, localeCode, includeYear)}`;
}

/** Localized weekday of an API calendar date, without timezone conversion. */
export function formatApiDateWeekday(value: string, localeCode: string): string {
  const iso = extractIsoDatePart(value);
  if (!iso) return '';
  const [year, month, day] = iso.split('-').map(Number);
  return new Intl.DateTimeFormat(localeCode, { weekday: 'long' }).format(new Date(year, month - 1, day));
}
